// src/pages/ConfiguracionFinanciera.js
import React, { useEffect, useState, useRef } from 'react';
import { doc, onSnapshot, updateDoc, getDoc, collection, getDocs, writeBatch, addDoc } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { httpsCallable } from 'firebase/functions';
import { Link } from 'react-router-dom';
import { FaDollarSign, FaQuestionCircle, FaArrowLeft, FaInfoCircle, FaExclamationTriangle, FaTrash } from 'react-icons/fa';
import './ConfiguracionFinanciera.css';
import ConfirmDialog from '../components/ConfirmDialog';
import MigracionMesesPendientes from '../components/MigracionMesesPendientes';
import ModalRestriccionServiciosMora, { DEFAULT_RESTRICCIONES_DETALLE } from '../components/ModalRestriccionServiciosMora';
import { calcularEstadoMoroso, esCasaCaseta } from '../utils/morosoUtils';

const defaultConfigState = {
    tipoCalculo: 'igual',
    cuotaExtraDescripcion: '',
    cuotaExtras: [],
    fondoReservaPercent: '10',
    fondoReservaEnabled: false,
    montoMensual: '',
    cuotaExtraMonto: '',
    consumoIndividualizado: false,
    consumoTipo: 'no',
    consumoOpciones: '',
    interesesMoratorios: '',
    multasAdministrativas: '',
    multasList: [],
    rentaAmenidades: '',
    etiquetasControles: '',
    consumoAgua: false,
    consumoGas: false,
    consumoInternet: false,
    consumoLuz: false,
    consumoAguaMonto: '',
    consumoGasMonto: '',
    consumoInternetMonto: '',
    consumoLuzMonto: '',
    consumoServiciosList: [], // Nueva lista dinámica de servicios
    discountEnabled: false,
    discountPercent: 0,
    discountDescription: '',
    multasEnabled: false,
    multasAmount: '',
    interesesEnabled: false,
    interesesType: 'fixed',
    interesesValue: '',
    interesesMesesPermitidosDeuda: '1',
    interesesAplicarA: [],
    restriccionServiciosEnabled: false,
    restriccionServicios: [],
    restriccionesDetalle: null,
    restriccionMeses: '',
    corteDia: '',
    corteDiaResidentes: '',
    sancionesInicioDia: '',
    annualBudgetEnabled: false,
    annualBudget: '',
    maintenanceAllocation: '',
    reserveAllocation: '',
    fundTargetPercent: 10,
    fundUsePolicy: '',
};

const parseBoolean = (value, fallback = false) => {
    if (value === undefined || value === null) return fallback;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        return normalized === 'true' || normalized === '1';
    }
    return Boolean(value);
};

const ConfiguracionFinanciera = () => {
    const { userData } = useAuth();
    const { showNotification } = useNotification();
    const fraccionamientoId = userData?.fraccionamientoId || userData?.fraccionamientoTd || null;
    const userRole = (userData?.rol || '').toLowerCase();
    // platformadmin es el rol de manager (jerarquía superior) que sí debe ver el botón
    const isPlatformAdmin = userRole === 'platformadmin';

    const [tab, setTab] = useState(0);
    const [config, setConfig] = useState(defaultConfigState);
    const [isSaving, setIsSaving] = useState(false);
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [cuotaExtrasErrors, setCuotaExtrasErrors] = useState([]);
    const [multasErrors, setMultasErrors] = useState([]);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [helpModal, setHelpModal] = useState({ open: false, title: '', content: '' });
    const [showAddMulta, setShowAddMulta] = useState(false);
    const [newMultaDesc, setNewMultaDesc] = useState('');
    const [newMultaMonto, setNewMultaMonto] = useState('');
    const [newMultaErrors, setNewMultaErrors] = useState({});
    const [pendingCuotaRemoval, setPendingCuotaRemoval] = useState(null);
    const [pendingMultaRemoval, setPendingMultaRemoval] = useState(null);
    const [pendingTabChange, setPendingTabChange] = useState(null);
    const [showTabChangeConfirm, setShowTabChangeConfirm] = useState(false);
    const [showFreezeConfirm, setShowFreezeConfirm] = useState(false);
    const [pendingFreezeValue, setPendingFreezeValue] = useState(false);
    const [showInterestInfoModal, setShowInterestInfoModal] = useState(false);
    const [showRestriccionesModal, setShowRestriccionesModal] = useState(false);
    const [modulosHabilitados, setModulosHabilitados] = useState({});
    const { currentUser } = useAuth();
    const [cuotasCollapsed, setCuotasCollapsed] = useState(false);
    const [shouldFocusNewExtra, setShouldFocusNewExtra] = useState(false);
    const lastExtraInputRef = useRef(null);

    const pageTopRef = useRef(null);
    const loadedRef = useRef(false);

    // Auto-enfoque al agregar una nueva cuota extraordinaria
    useEffect(() => {
        if (shouldFocusNewExtra && lastExtraInputRef.current) {
            lastExtraInputRef.current.focus();
            setShouldFocusNewExtra(false);
        }
    }, [shouldFocusNewExtra, config.cuotaExtras]);
    const saveTimerRef = useRef(null);
    const originalConfigRef = useRef(null);
    // Campos por sección para detectar cambios solo en la pestaña activa
    const SECTION_FIELDS = useRef({
        0: [
            'tipoCalculo', 'montoMensual', 'cuotaExtraDescripcion', 'cuotaExtraMonto', 'cuotaExtras',
            'consumoIndividualizado', 'consumoTipo', 'consumoOpciones',
            'consumoAgua', 'consumoGas', 'consumoInternet', 'consumoLuz',
            'consumoAguaMonto', 'consumoGasMonto', 'consumoInternetMonto', 'consumoLuzMonto',
            'consumoServiciosList',
            'corteDia', 'corteDiaResidentes', 'sancionesInicioDia',
            'congelarCuentasHeredadas'
        ],
        1: [
            'discountEnabled', 'discountPercent', 'discountDescription',
            'multasEnabled', 'multasAmount', 'multasAdministrativas', 'multasList',
            'interesesEnabled', 'interesesType', 'interesesValue', 'interesesMesesPermitidosDeuda', 'interesesAplicarA',
            'restriccionServiciosEnabled', 'restriccionServicios', 'restriccionesDetalle', 'restriccionMeses'
        ],
        2: [
            'annualBudgetEnabled', 'maintenanceAllocation', 'securityCleaningAllocation', 'utilitiesAllocation',
            'fondoReservaPercent', 'reserveAllocation', 'fundUsePolicy'
        ]
    });

    const openHelp = (title, content) => setHelpModal({ open: true, title, content });
    const closeHelp = () => setHelpModal({ open: false, title: '', content: '' });
    const onHelpKeyDown = (e, title, content) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openHelp(title, content);
        }
    };

    const hasUnsavedChangesInSection = (sectionIndex) => {
        if (!originalConfigRef.current) return false;
        const keys = SECTION_FIELDS.current[sectionIndex] || [];
        for (const key of keys) {
            const curr = config[key];
            const base = originalConfigRef.current[key];

            // Comparison logic
            if (curr === base) continue;

            // If both can be numbers, compare them as numbers (handles "100" vs "100.00" or string vs number)
            const numCurr = Number(curr);
            const numBase = Number(base);
            if (
                curr !== '' && base !== '' &&
                curr !== null && base !== null &&
                !isNaN(numCurr) && !isNaN(numBase) &&
                typeof curr !== 'boolean' && typeof base !== 'boolean' &&
                !Array.isArray(curr) && !Array.isArray(base)
            ) {
                if (Math.abs(numCurr - numBase) > 0.000001) return true;
                continue;
            }

            // JSON stringify guards arrays/objects
            if (JSON.stringify(curr) !== JSON.stringify(base)) return true;
        }
        return false;
    };

    const handleTabChange = (newTab) => {
        // Solo alertar si la pestaña actual tiene cambios sin guardar y no se está guardando actualmente
        if (hasUnsavedChangesInSection(tab) && !isSaving) {
            setPendingTabChange(newTab);
            setShowTabChangeConfirm(true);
        } else {
            setTab(newTab);
        }
    };

    const confirmTabChange = () => {
        if (pendingTabChange !== null) {
            setTab(pendingTabChange);
            setPendingTabChange(null);
        }
        setShowTabChangeConfirm(false);
    };

    const cancelTabChange = () => {
        setPendingTabChange(null);
        setShowTabChangeConfirm(false);
    };

    const handleFreezeToggle = (newValue) => {
        if (newValue === true) {
            // Si está activando, mostrar modal de confirmación
            setPendingFreezeValue(true);
            setShowFreezeConfirm(true);
        } else {
            // Si está desactivando, aplicar directamente
            setConfigField('congelarCuentasHeredadas', false);
        }
    };

    const confirmFreeze = () => {
        setConfigField('congelarCuentasHeredadas', pendingFreezeValue);
        setShowFreezeConfirm(false);
        setPendingFreezeValue(false);
    };

    const cancelFreeze = () => {
        setShowFreezeConfirm(false);
        setPendingFreezeValue(false);
    };

    const handleInterestToggle = (newValue) => {
        if (newValue === true) {
            // Validar que se haya configurado el día de inicio de sanciones
            if (!config.sancionesInicioDia || config.sancionesInicioDia === '') {
                showNotification('Debes establecer el día de inicio de sanciones en la pestaña "Reglas y Corte" antes de habilitar intereses moratorios', 'error');
                return;
            }
            // Mostrar modal informativo al activar
            setShowInterestInfoModal(true);
        } else {
            // Al desactivar, aplicar directamente
            setConfigField('interesesEnabled', false);
        }
    };

    const confirmInterestActivation = () => {
        setConfigField('interesesEnabled', true);
        setShowInterestInfoModal(false);
    };

    const cancelInterestActivation = () => {
        setShowInterestInfoModal(false);
    };

    const validateField = (field, value) => {
        setFormErrors((prev) => {
            const next = { ...prev };
            const asNumber = (v) => {
                const n = Number(v);
                return Number.isNaN(n) ? null : n;
            };
            switch (field) {
                case 'montoMensual': {
                    const n = asNumber(value);
                    if (value !== '' && (n === null || n < 0)) next.montoMensual = 'Ingrese un monto válido (>= 0)';
                    else delete next.montoMensual;
                    break;
                }
                case 'discountPercent': {
                    const n = asNumber(value);
                    if (value !== '' && (n === null || n < 0 || n > 100)) next.discountPercent = 'Ingrese un porcentaje entre 0 y 100';
                    else delete next.discountPercent;
                    break;
                }
                case 'interesesValue': {
                    const n = asNumber(value);
                    if (config.interesesEnabled && (value === '' || n === null || n < 0)) next.interesesValue = 'Ingrese un valor válido (>= 0)';
                    else delete next.interesesValue;
                    break;
                }
                case 'interesesMesesPermitidosDeuda': {
                    const n = parseInt(value, 10);
                    if (config.interesesEnabled && (value === '' || isNaN(n) || n < 0 || n > 12)) next.interesesMesesPermitidosDeuda = 'Ingrese un número entre 0 y 12 meses';
                    else delete next.interesesMesesPermitidosDeuda;
                    break;
                }
                case 'fondoReservaPercent': {
                    const n = asNumber(value);
                    if (config.fondoReservaEnabled && (value === '' || n === null || n < 0 || n > 100)) next.fondoReservaPercent = 'Ingrese un porcentaje entre 0 y 100';
                    else delete next.fondoReservaPercent;
                    break;
                }
                case 'fundTargetPercent': {
                    const n = asNumber(value);
                    if (value !== '' && (n === null || n < 0 || n > 100)) next.fundTargetPercent = 'Ingrese un porcentaje entre 0 y 100';
                    else delete next.fundTargetPercent;
                    break;
                }
                case 'annualBudget': {
                    const n = asNumber(value);
                    if (config.annualBudgetEnabled && (value === '' || n === null || n < 0)) next.annualBudget = 'Ingrese un monto válido (>= 0)';
                    else delete next.annualBudget;
                    break;
                }
                case 'maintenanceAllocation': {
                    const n = asNumber(value);
                    if (config.annualBudgetEnabled) {
                        if (value !== '') {
                            if (n === null || n < 0) next.maintenanceAllocation = 'Ingrese un monto válido (>= 0)';
                            else delete next.maintenanceAllocation;
                        } else delete next.maintenanceAllocation;
                    } else delete next.maintenanceAllocation;
                    break;
                }
                case 'reserveAllocation': {
                    const n = asNumber(value);
                    if (config.annualBudgetEnabled) {
                        if (value !== '') {
                            if (n === null || n < 0) next.reserveAllocation = 'Ingrese un monto válido (>= 0)';
                            else delete next.reserveAllocation;
                        } else delete next.reserveAllocation;
                    } else delete next.reserveAllocation;
                    break;
                }
                case 'consumoAguaMonto':
                case 'consumoGasMonto':
                case 'consumoInternetMonto':
                case 'consumoLuzMonto': {
                    const map = {
                        consumoAguaMonto: 'consumoAgua',
                        consumoGasMonto: 'consumoGas',
                        consumoInternetMonto: 'consumoInternet',
                        consumoLuzMonto: 'consumoLuz',
                    };
                    if (config.consumoTipo !== 'no' && config[map[field]]) {
                        const n = asNumber(value);
                        if (value === '' || n === null || n < 0) next[field] = 'Ingrese un monto válido (>= 0)';
                        else delete next[field];
                    } else {
                        delete next[field];
                    }
                    break;
                }
                case 'corteDia':
                case 'sancionesInicioDia': {
                    if (value !== '') {
                        const n = Number(value);
                        if (!Number.isInteger(n) || n < 1 || n > 31) next[field] = 'Selecciona un día válido (1-31)';
                        else delete next[field];
                    } else delete next[field];
                    break;
                }
                case 'restriccionMeses': {
                    if (config.restriccionServiciosEnabled) {
                        const n = Number(value);
                        if (value === '' || Number.isNaN(n) || !Number.isInteger(n) || n < 0) next.restriccionMeses = 'Ingrese un número entero de meses (>= 0)';
                        else delete next.restriccionMeses;
                    } else delete next.restriccionMeses;
                    break;
                }
                default:
                    break;
            }
            return next;
        });
    };

    const setConfigField = (field, value) => {
        setConfig((prev) => ({ ...prev, [field]: value }));
        validateField(field, value);
    };

    const addCuotaExtra = () => {
        const mesActual = new Date().getMonth() + 1; // 1-12
        setConfig((prev) => ({
            ...prev,
            cuotaExtras: [...(Array.isArray(prev.cuotaExtras) ? prev.cuotaExtras : []), { 
                id: Date.now(), 
                descripcion: '', 
                monto: '', 
                frecuencia: 'monthly',
                mesInicio: mesActual
            }],
        }));
        setCuotaExtrasErrors((prev) => [...prev, {}]);
        setShouldFocusNewExtra(true);
        setCuotasCollapsed(false); // Expandir al agregar una nueva
    };

    const updateCuotaExtra = (index, field, value) => {
        let sanitizedValue = value;
        if (field === 'descripcion') {
            // Solo letras, números y espacios
            sanitizedValue = value.replace(/[^a-zA-Z0-9\s]/g, '');
        } else if (field === 'monto') {
            // Solo números y punto decimal
            sanitizedValue = value.replace(/[^0-9.]/g, '');
        }

        setConfig((prev) => {
            const list = Array.isArray(prev.cuotaExtras) ? [...prev.cuotaExtras] : [];
            list[index] = { ...list[index], [field]: sanitizedValue };
            return { ...prev, cuotaExtras: list };
        });
        setCuotaExtrasErrors((prev) => {
            const next = [...(prev || [])];
            next[index] = next[index] || {};
            if (field === 'descripcion') {
                if (value && value.trim()) delete next[index].descripcion;
            }
            if (field === 'monto') {
                const n = Number(value);
                if (value === '' || Number.isNaN(n) || n < 0) next[index].monto = 'Ingrese un monto válido (>= 0)';
                else delete next[index].monto;
            }
            return next;
        });
    };

    const removeCuotaExtra = (index) => {
        setConfig((prev) => {
            const list = Array.isArray(prev.cuotaExtras) ? [...prev.cuotaExtras] : [];
            list.splice(index, 1);
            return { ...prev, cuotaExtras: list };
        });
        setCuotaExtrasErrors((prev) => {
            const next = Array.isArray(prev) ? [...prev] : [];
            next.splice(index, 1);
            return next;
        });
    };

    const requestRemoveCuotaExtra = (index) => {
        setPendingCuotaRemoval(index);
    };

    const confirmRemoveCuotaExtra = () => {
        if (pendingCuotaRemoval === null) return;
        removeCuotaExtra(pendingCuotaRemoval);
        setPendingCuotaRemoval(null);
    };

    const cancelRemoveCuotaExtra = () => {
        setPendingCuotaRemoval(null);
    };

    const requestRemoveMulta = (index) => {
        setPendingMultaRemoval(index);
    };

    const addConsumoServicio = () => {
        const nuevo = { id: Date.now(), nombre: '', monto: '', habilitado: true };
        setConfig(prev => ({
            ...prev,
            consumoServiciosList: [...(prev.consumoServiciosList || []), nuevo]
        }));
    };

    const removeConsumoServicio = (index) => {
        setConfig(prev => {
            const list = [...(prev.consumoServiciosList || [])];
            list.splice(index, 1);
            return { ...prev, consumoServiciosList: list };
        });
    };

    const updateConsumoServicio = (index, field, value) => {
        setConfig(prev => {
            const list = [...(prev.consumoServiciosList || [])];
            list[index] = { ...list[index], [field]: value };
            return { ...prev, consumoServiciosList: list };
        });
    };

    const confirmRemoveMulta = () => {
        if (pendingMultaRemoval === null) return;
        const index = pendingMultaRemoval;
        setConfig((prev) => {
            const next = Array.isArray(prev.multasList) ? [...prev.multasList] : [];
            next.splice(index, 1);
            return { ...prev, multasList: next };
        });
        setMultasErrors((prev) => {
            if (!Array.isArray(prev)) return [];
            const next = [...prev];
            next.splice(index, 1);
            return next;
        });
        setPendingMultaRemoval(null);
    };

    const cancelRemoveMulta = () => {
        setPendingMultaRemoval(null);
    };

    const confirmDiscardChanges = async () => {
        if (isSaving) return;
        setShowCancelConfirm(false);
        await cancelChanges();
    };

    const runFullValidation = () => {
        const nextFormErrors = {};

        const asNumber = (value) => {
            const num = Number(value);
            return Number.isNaN(num) ? null : num;
        };

        if (config.montoMensual !== '') {
            const val = asNumber(config.montoMensual);
            if (val === null || val < 0) nextFormErrors.montoMensual = 'Ingrese un monto válido (>= 0)';
        }

        if (config.discountPercent !== undefined && config.discountEnabled) {
            const val = asNumber(config.discountPercent);
            if (config.discountPercent === '' || val === null || val < 0 || val > 100) nextFormErrors.discountPercent = 'Ingrese un porcentaje entre 0 y 100';
        }

        if (config.interesesEnabled) {
            const val = asNumber(config.interesesValue);
            if (config.interesesValue === '' || val === null || val < 0) nextFormErrors.interesesValue = 'Ingrese un valor válido (>= 0)';

            const meses = parseInt(config.interesesMesesPermitidosDeuda, 10);
            if (config.interesesMesesPermitidosDeuda === '' || isNaN(meses) || meses < 0 || meses > 12) nextFormErrors.interesesMesesPermitidosDeuda = 'Ingrese un número entre 0 y 12 meses';
        }

        if (config.annualBudgetEnabled) {
            const val = asNumber(config.fondoReservaPercent);
            if (config.fondoReservaPercent === '' || val === null || val < 0 || val > 100) nextFormErrors.fondoReservaPercent = 'Ingrese un porcentaje entre 0 y 100';
        }

        if (config.annualBudgetEnabled) {
            const maintenance = asNumber(config.maintenanceAllocation);
            if (config.maintenanceAllocation !== '' && (maintenance === null || maintenance < 0)) {
                nextFormErrors.maintenanceAllocation = 'Ingrese un monto válido (>= 0)';
            }

            const security = asNumber(config.securityCleaningAllocation);
            if (config.securityCleaningAllocation !== '' && (security === null || security < 0)) {
                nextFormErrors.securityCleaningAllocation = 'Ingrese un monto válido (>= 0)';
            }

            const utilities = asNumber(config.utilitiesAllocation);
            if (config.utilitiesAllocation !== '' && (utilities === null || utilities < 0)) {
                nextFormErrors.utilitiesAllocation = 'Ingrese un monto válido (>= 0)';
            }

            const reserve = asNumber(config.reserveAllocation);
            if (config.reserveAllocation !== '' && (reserve === null || reserve < 0)) {
                nextFormErrors.reserveAllocation = 'Ingrese un monto válido (>= 0)';
            }
        }

        if (config.consumoTipo !== 'no') {
            if (config.consumoAgua) {
                const val = asNumber(config.consumoAguaMonto);
                if (config.consumoAguaMonto === '' || val === null || val < 0) nextFormErrors.consumoAguaMonto = 'Ingrese un monto válido (>= 0)';
            }
            if (config.consumoGas) {
                const val = asNumber(config.consumoGasMonto);
                if (config.consumoGasMonto === '' || val === null || val < 0) nextFormErrors.consumoGasMonto = 'Ingrese un monto válido (>= 0)';
            }
            if (config.consumoInternet) {
                const val = asNumber(config.consumoInternetMonto);
                if (config.consumoInternetMonto === '' || val === null || val < 0) nextFormErrors.consumoInternetMonto = 'Ingrese un monto válido (>= 0)';
            }
            if (config.consumoLuz) {
                const val = asNumber(config.consumoLuzMonto);
                if (config.consumoLuzMonto === '' || val === null || val < 0) nextFormErrors.consumoLuzMonto = 'Ingrese un monto válido (>= 0)';
            }
        }

        if (config.corteDia) {
            const n = Number(config.corteDia);
            if (!Number.isInteger(n) || n < 1 || n > 28) nextFormErrors.corteDia = 'Selecciona un día válido (1-28)';
        }

        if (config.sancionesInicioDia) {
            const n = Number(config.sancionesInicioDia);
            if (!Number.isInteger(n) || n < 1 || n > 28) {
                nextFormErrors.sancionesInicioDia = 'Selecciona un día válido (1-28)';
            } else if (config.corteDia) {
                const corte = Number(config.corteDia);
                if (n <= corte) {
                    nextFormErrors.sancionesInicioDia = 'El día de inicio de sanciones debe ser posterior al día de corte';
                }
            }
        }

        if (config.restriccionServiciosEnabled) {
            const n = Number(config.restriccionMeses);
            if (config.restriccionMeses === '' || Number.isNaN(n) || !Number.isInteger(n) || n < 0) {
                nextFormErrors.restriccionMeses = 'Ingrese un número entero de meses (>= 0)';
            }
        }

        setFormErrors(nextFormErrors);

        const extraErrors = (config.cuotaExtras || []).map(() => ({}));
        (config.cuotaExtras || []).forEach((c, idx) => {
            if (!c.descripcion || !c.descripcion.trim()) extraErrors[idx].descripcion = 'La descripción es obligatoria';
            const num = asNumber(c.monto);
            if (c.monto === '' || num === null || num < 0) extraErrors[idx].monto = 'Ingrese un monto válido (>= 0)';
            const allowed = ['monthly', 'quarterly', 'bimonthly', 'semiannual', 'yearly'];
            if (!allowed.includes(c.frecuencia)) extraErrors[idx].frecuencia = 'Frecuencia inválida';
        });
        setCuotaExtrasErrors(extraErrors);

        const multasCheck = (config.multasList || []).map(() => ({}));
        (config.multasList || []).forEach((m, idx) => {
            if (!m.descripcion || !m.descripcion.trim()) multasCheck[idx].descripcion = 'La descripción es obligatoria';
            const num = asNumber(m.monto);
            if (m.monto === '' || num === null || num < 0) multasCheck[idx].monto = 'Ingrese un monto válido (>= 0)';
        });
        setMultasErrors(multasCheck);

        const hasFormErrors = Object.keys(nextFormErrors).length > 0;
        const hasCuotaErrors = extraErrors.some((entry) => entry && Object.keys(entry).length > 0);
        const hasMultasErrors = multasCheck.some((entry) => entry && Object.keys(entry).length > 0);
        return !(hasFormErrors || hasCuotaErrors || hasMultasErrors);
    };

    const saveExtraFeesOnly = async () => {
        if (!fraccionamientoId) {
            showNotification('No hay fraccionamiento seleccionado', 'error');
            return;
        }

        // Validar solo extraordinarias
        const extraErrors = (config.cuotaExtras || []).map(() => ({}));
        let hasErrors = false;
        (config.cuotaExtras || []).forEach((c, idx) => {
            if (!c.descripcion || !c.descripcion.trim()) {
                extraErrors[idx].descripcion = 'La descripción es obligatoria';
                hasErrors = true;
            }
            const num = Number(c.monto);
            if (c.monto === '' || Number.isNaN(num) || num < 0) {
                extraErrors[idx].monto = 'Ingrese un monto válido (>= 0)';
                hasErrors = true;
            }
        });
        setCuotaExtrasErrors(extraErrors);

        if (hasErrors) {
            showNotification('Corrige los errores en las cuotas extraordinarias', 'error');
            return;
        }

        try {
            setIsSaving(true);
            const ref = doc(db, 'fraccionamientos', fraccionamientoId);
            const payload = {
                cuotaExtras: (config.cuotaExtras || []).map((c) => ({
                    ...c,
                    monto: Number(c.monto || 0),
                    mesInicio: Number(c.mesInicio)
                })),
                lastSyncExtra: Date.now() // Forzar disparo del trigger
            };

            await updateDoc(ref, { 
                "configuracionFinanciera.cuotaExtras": payload.cuotaExtras,
                "configuracionFinanciera.lastSyncExtra": payload.lastSyncExtra
            });

            setCuotasCollapsed(true);
            originalConfigRef.current = { ...config };
            showNotification('Cuotas extraordinarias guardadas y sincronizadas correctamente', 'success');
        } catch (err) {
            console.error('ConfiguracionFinanciera: saveExtraFeesOnly error', err);
            showNotification('Error al sincronizar cuotas extraordinarias', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const saveNow = async () => {
        if (!fraccionamientoId) {
            showNotification('No hay fraccionamiento seleccionado', 'error');
            return;
        }

        // Validación específica para intereses moratorios
        if (config.interesesEnabled) {
            if (!config.interesesType || config.interesesType === '') {
                showNotification('Selecciona el tipo de interés antes de guardar', 'error');
                return;
            }
            if (config.interesesValue === '' || config.interesesValue === null || config.interesesValue === undefined) {
                showNotification('Ingresa el valor del interés antes de guardar', 'error');
                return;
            }
            if (config.interesesMesesPermitidosDeuda === '' || config.interesesMesesPermitidosDeuda === null) {
                showNotification('Ingresa los meses permitidos de deuda antes de guardar', 'error');
                return;
            }
            if (!config.sancionesInicioDia || config.sancionesInicioDia === '') {
                showNotification('Debes establecer el día de inicio de sanciones en la pestaña "Reglas y Corte" antes de habilitar intereses moratorios', 'error');
                return;
            }
        }

        // run full validation before saving
        const valid = runFullValidation();
        if (!valid) {
            showNotification('Corrige los errores antes de guardar', 'error');
            return;
        }
        try {
            setIsSaving(true);
            const ref = doc(db, 'fraccionamientos', fraccionamientoId);
            // Normalize numeric fields before saving
            const payload = {
                ...config,
                montoMensual: Number(config.montoMensual || 0),
                cuotaExtraMonto: Number(config.cuotaExtraMonto || 0),
                cuotaExtras: (config.cuotaExtras || []).map((c) => ({
                    ...c,
                    monto: Number(c.monto || 0),
                })),
                consumoAguaMonto: Number(config.consumoAguaMonto || 0),
                consumoGasMonto: Number(config.consumoGasMonto || 0),
                consumoInternetMonto: Number(config.consumoInternetMonto || 0),
                consumoLuzMonto: Number(config.consumoLuzMonto || 0),
                consumoServiciosList: (config.consumoServiciosList || []).map(s => ({
                    ...s,
                    monto: Number(s.monto || 0),
                    habilitado: Boolean(s.habilitado)
                })),
                // Actualizar consumoIndividualizado basado en consumoTipo
                consumoIndividualizado: config.consumoTipo && config.consumoTipo !== 'no',
                consumoTipo: config.consumoTipo || 'no',
                consumoOpciones: config.consumoOpciones || '',
                // Asegurar que los booleanos de consumo se guarden correctamente
                consumoAgua: Boolean(config.consumoAgua),
                consumoGas: Boolean(config.consumoGas),
                consumoInternet: Boolean(config.consumoInternet),
                consumoLuz: Boolean(config.consumoLuz),
                fondoReservaPercent: Number(config.fondoReservaPercent || 0),
                corteDia: config.corteDia ? Number(config.corteDia) : null,
                corteDiaResidentes: config.corteDiaResidentes ? Number(config.corteDiaResidentes) : (config.corteDia ? Number(config.corteDia) : null),
                sancionesInicioDia: config.sancionesInicioDia ? Number(config.sancionesInicioDia) : null,
                // descuentos / sanciones normalization
                discountEnabled: Boolean(config.discountEnabled),
                discountPercent: config.discountPercent !== '' ? Number(config.discountPercent) : 0,
                discountDescription: config.discountDescription || '',
                multasEnabled: Boolean(config.multasEnabled),
                multasAmount: (() => {
                    if (Array.isArray(config.multasList) && config.multasList.length > 0) return Number(config.multasList[0].monto || 0);
                    return config.multasAmount !== '' ? Number(config.multasAmount) : 0;
                })(),
                multasAdministrativas: (() => {
                    if (Array.isArray(config.multasList) && config.multasList.length > 0) return config.multasList[0].descripcion || '';
                    return config.multasAdministrativas || '';
                })(),
                multasAdministrativasList: Array.isArray(config.multasList) ? config.multasList.map((m) => ({ id: m.id || Date.now(), descripcion: m.descripcion || '', monto: Number(m.monto || 0) })) : [],
                interesesEnabled: Boolean(config.interesesEnabled),
                interesesType: config.interesesType || 'fixed',
                interesesValue: config.interesesValue !== '' ? Number(config.interesesValue) : 0,
                interesesMesesPermitidosDeuda: config.interesesMesesPermitidosDeuda !== '' && !isNaN(parseInt(config.interesesMesesPermitidosDeuda, 10)) ? parseInt(config.interesesMesesPermitidosDeuda, 10) : 1,
                interesesAplicarA: Array.isArray(config.interesesAplicarA) ? config.interesesAplicarA : [],
                // restricción servicios
                restriccionServiciosEnabled: Boolean(config.restriccionServiciosEnabled),
                restriccionServicios: Array.isArray(config.restriccionServicios) ? config.restriccionServicios : [],
                restriccionesDetalle: config.restriccionesDetalle || null,
                restriccionMeses: config.restriccionMeses !== '' && !isNaN(parseInt(config.restriccionMeses, 10)) ? parseInt(config.restriccionMeses, 10) : 0,
                annualBudgetEnabled: Boolean(config.annualBudgetEnabled),
                maintenanceAllocation: Number(config.maintenanceAllocation || 0),
                securityCleaningAllocation: Number(config.securityCleaningAllocation || 0),
                utilitiesAllocation: Number(config.utilitiesAllocation || 0),
                reserveAllocation: Number(config.reserveAllocation || 0),
                fundUsePolicy: config.fundUsePolicy || '',
                // congelamiento de cuentas heredadas
                congelarCuentasHeredadas: Boolean(config.congelarCuentasHeredadas),
            };
            await updateDoc(ref, { configuracionFinanciera: payload });

            // Sincronización masiva de meses de gracia en casas
            try {
                const mesesGracia = Number(payload.interesesMesesPermitidosDeuda ?? 1);
                const cuotaMto = Number(payload.montoMensual || payload.cuotaMensual || 0);
                const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
                const casasSnap = await getDocs(casasRef);

                if (!casasSnap.empty) {
                    let batch = writeBatch(db);
                    let count = 0;

                    for (const casaDoc of casasSnap.docs) {
                        const cData = casaDoc.data();
                        if (esCasaCaseta(cData)) continue;

                        const cAdeudo = Number(cData.adeudo || 0);
                        const esMorosoCalculado = (cData.saldoFavor || 0) > 0
                            ? false
                            : calcularEstadoMoroso(cAdeudo, cuotaMto, mesesGracia);

                        const updates = {
                            mesesPermitidosGracia: mesesGracia,
                            moroso: esMorosoCalculado
                        };
                        batch.update(casaDoc.ref, updates);
                        count++;

                        if (count === 500) {
                            await batch.commit();
                            batch = writeBatch(db);
                            count = 0;
                        }
                    }

                    if (count > 0) {
                        await batch.commit();
                    }
                }
            } catch (syncErr) {
                console.error('Error en sincronización masiva de meses de gracia:', syncErr);
                // No bloqueamos el éxito del guardado principal, pero informamos en consola
            }

            // 1. Detectar cambios en el config base
            const oldConfig = originalConfigRef.current || {};
            const keysChanged = Object.keys(config).filter(key => JSON.stringify(oldConfig[key]) !== JSON.stringify(config[key]));

            // 2. Construir Resumen Activo (Siempre las mismas llaves)
            const resumen = {
                montoMensual: config.montoMensual || '',
                corteDia: config.corteDia || '',
                sancionesInicioDia: config.sancionesInicioDia || '',
                cuotaExtras: (config.cuotaExtras && config.cuotaExtras.length > 0) 
                    ? config.cuotaExtras.map(c => `${c.descripcion}: $${c.monto}`).join(' | ') 
                    : 'Ninguna',
                interesesEnabled: config.interesesEnabled,
                interesesType: config.interesesEnabled ? config.interesesType : '',
                interesesValue: config.interesesEnabled ? config.interesesValue : '',
                interesesMesesPermitidosDeuda: config.interesesEnabled ? config.interesesMesesPermitidosDeuda : '',
                multasEnabled: config.multasEnabled,
                multas: config.multasEnabled && Array.isArray(config.multasList) && config.multasList.length > 0
                    ? config.multasList.map(m => `${m.descripcion} ($${m.monto})`).join(' | ') 
                    : (config.multasEnabled ? `${config.multasAdministrativas || 'General'} ($${config.multasAmount || 0})` : ''),
                restriccionServiciosEnabled: config.restriccionServiciosEnabled,
                restriccionMeses: config.restriccionServiciosEnabled ? config.restriccionMeses : '',
                restriccionServicios: config.restriccionServiciosEnabled && Array.isArray(config.restriccionServicios) 
                    ? config.restriccionServicios.join(', ') : '',
                consumosExtra: (config.consumoIndividualizado || (config.consumoTipo && config.consumoTipo !== 'no')) ? 'Activado' : 'Desactivado'
            };

            // 3. Mapear qué llaves del resumen deben resaltarse
            const modificados = [];
            const checkMod = (configKeys, resumenKey) => {
                if (configKeys.some(k => keysChanged.includes(k))) modificados.push(resumenKey);
            };

            checkMod(['montoMensual'], 'montoMensual');
            checkMod(['corteDia'], 'corteDia');
            checkMod(['sancionesInicioDia'], 'sancionesInicioDia');
            checkMod(['cuotaExtras'], 'cuotaExtras');
            checkMod(['interesesEnabled'], 'interesesEnabled');
            checkMod(['interesesType'], 'interesesType');
            checkMod(['interesesValue'], 'interesesValue');
            checkMod(['interesesMesesPermitidosDeuda'], 'interesesMesesPermitidosDeuda');
            checkMod(['multasEnabled', 'multasList', 'multasAdministrativas', 'multasAmount'], 'multasEnabled');
            checkMod(['multasList', 'multasAdministrativas', 'multasAmount'], 'multas');
            checkMod(['restriccionServiciosEnabled'], 'restriccionServiciosEnabled');
            checkMod(['restriccionMeses'], 'restriccionMeses');
            checkMod(['restriccionServicios'], 'restriccionServicios');
            checkMod(['consumoIndividualizado', 'consumoTipo', 'consumoOpciones'], 'consumosExtra');

            setLastSavedAt(Date.now());
            // Guardamos el estado actual tal cual (con strings) para evitar falsos positivos de cambios
            originalConfigRef.current = { ...config };

            // Registrar en el dashboard de notificaciones
            try {
                await addDoc(collection(db, 'fraccionamientos', fraccionamientoId, 'notificaciones'), {
                    fecha: new Date(),
                    titulo: 'Configuración Financiera Actualizada',
                    descripcion: `El administrador ${userData?.nombre || 'Desconocido'} ha actualizado las configuraciones de finanzas (cuotas, reglas, recargos).`,
                    actorNombre: userData?.nombre || 'Administrador',
                    actorRol: userData?.rol || 'admin',
                    actorRolGrupo: 'administrador',
                    categoria: 'sistema',
                    tipoMovimiento: 'configuracion_financiera',
                    origen: 'web',
                    detalles: { resumen, modificados }
                });
            } catch (notifErr) {
                console.error('Error al registrar notificación de sistema:', notifErr);
            }

            showNotification('Configuración financiera guardada y sincronizada con residentes', 'success');
        } catch (err) {
            console.error('ConfiguracionFinanciera: saveNow error', err);
            showNotification('No se pudo guardar la configuración financiera', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const cancelChanges = async () => {
        if (!fraccionamientoId) {
            showNotification('No hay fraccionamiento seleccionado', 'error');
            return;
        }
        if (isSaving) return;
        try {
            const ref = doc(db, 'fraccionamientos', fraccionamientoId);
            const snap = await getDoc(ref);
            const data = snap.exists() ? (snap.data().configuracionFinanciera || {}) : {};
            const normalized = {
                ...data,
                montoMensual: data.montoMensual != null ? String(data.montoMensual) : '',
                cuotaExtraMonto: data.cuotaExtraMonto != null ? String(data.cuotaExtraMonto) : '',
                cuotaExtras: (data.cuotaExtras && Array.isArray(data.cuotaExtras)) ? data.cuotaExtras.map((c) => ({ ...c, monto: c.monto != null ? String(c.monto) : '' })) : (data.cuotaExtraDescripcion ? [{ descripcion: data.cuotaExtraDescripcion || '', monto: data.cuotaExtraMonto != null ? String(data.cuotaExtraMonto) : '', frecuencia: 'monthly' }] : []),
                consumoAguaMonto: data.consumoAguaMonto != null ? String(data.consumoAguaMonto) : '',
                consumoGasMonto: data.consumoGasMonto != null ? String(data.consumoGasMonto) : '',
                consumoInternetMonto: data.consumoInternetMonto != null ? String(data.consumoInternetMonto) : '',
                consumoLuzMonto: data.consumoLuzMonto != null ? String(data.consumoLuzMonto) : '',
                fondoReservaPercent: data.fondoReservaPercent != null ? String(data.fondoReservaPercent) : '10',
                corteDia: data.corteDia != null ? String(data.corteDia) : '',
                corteDiaResidentes: data.corteDiaResidentes != null ? String(data.corteDiaResidentes) : '',
                sancionesInicioDia: data.sancionesInicioDia != null ? String(data.sancionesInicioDia) : '',
                annualBudgetEnabled: parseBoolean(data.annualBudgetEnabled, data.annualBudget != null && data.annualBudget !== ''),
                annualBudget: data.annualBudget != null ? String(data.annualBudget) : '',
                maintenanceAllocation: data.maintenanceAllocation != null ? String(data.maintenanceAllocation) : '',
                reserveAllocation: data.reserveAllocation != null ? String(data.reserveAllocation) : '',
                // descuentos/multas/intereses
                discountEnabled: data.discountEnabled || false,
                discountPercent: data.discountPercent != null ? String(data.discountPercent) : '',
                discountDescription: data.discountDescription || '',
                multasEnabled: data.multasEnabled || false,
                multasAmount: data.multasAmount != null ? String(data.multasAmount) : '',
                multasAdministrativas: data.multasAdministrativas || '',
                multasList: Array.isArray(data.multasAdministrativasList) ? data.multasAdministrativasList.map((m, i) => ({ id: m.id || i, descripcion: m.descripcion || '', monto: m.monto != null ? String(m.monto) : '' })) : (data.multasAdministrativas ? [{ id: Date.now(), descripcion: data.multasAdministrativas || '', monto: data.multasAmount != null ? String(data.multasAmount) : '' }] : []),
                interesesEnabled: data.interesesEnabled || false,
                interesesType: data.interesesType || 'fixed',
                interesesValue: data.interesesValue != null ? String(data.interesesValue) : '',
                interesesMesesPermitidosDeuda: data.interesesMesesPermitidosDeuda != null ? String(data.interesesMesesPermitidosDeuda) : '1',
                interesesAplicarA: Array.isArray(data.interesesAplicarA) ? data.interesesAplicarA : [],
                // restricción de servicios
                restriccionServiciosEnabled: data.restriccionServiciosEnabled || false,
                restriccionServicios: Array.isArray(data.restriccionServicios) ? data.restriccionServicios : [],
                restriccionesDetalle: data.restriccionesDetalle || null,
                restriccionMeses: data.restriccionMeses != null ? String(data.restriccionMeses) : '0',
                // congelamiento de cuentas heredadas
                congelarCuentasHeredadas: data.congelarCuentasHeredadas || false,
            };
            setConfig((p) => ({ ...p, ...normalized }));
            // Baseline normalizada para detectar cambios de forma consistente
            originalConfigRef.current = { ...normalized };
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
                saveTimerRef.current = null;
            }
            showNotification('Cambios revertidos a la última versión guardada', 'info');
        } catch (err) {
            console.error('ConfiguracionFinanciera: cancelChanges error', err);
            showNotification('No se pudo restaurar la configuración', 'error');
        }
    };

    const handleCancelClick = () => {
        const orig = originalConfigRef.current || {};
        try {
            if (JSON.stringify(orig) === JSON.stringify(config)) {
                // no local changes to discard, just restore latest from server
                cancelChanges();
                return;
            }
        } catch (e) {
            // if comparison fails, show confirmation
        }
        setShowCancelConfirm(true);
    };

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape' && helpModal.open) closeHelp();
        };
        if (helpModal.open) {
            document.addEventListener('keydown', onKey);
        }
        return () => document.removeEventListener('keydown', onKey);
    }, [helpModal.open]);

    useEffect(() => {
        if (!fraccionamientoId) return undefined;
        const ref = doc(db, 'fraccionamientos', fraccionamientoId);
        const unsub = onSnapshot(ref, (snap) => {
            const data = snap.data() || {};
            const cfg = data.configuracionFinanciera || {};
            // normalize numeric fields into strings for form inputs
            const normalized = {
                ...cfg,
                montoMensual: cfg.montoMensual != null ? String(cfg.montoMensual) : '',
                cuotaExtraMonto: cfg.cuotaExtraMonto != null ? String(cfg.cuotaExtraMonto) : '',
                cuotaExtras: (cfg.cuotaExtras && Array.isArray(cfg.cuotaExtras)) ? cfg.cuotaExtras.map((c) => ({ ...c, monto: c.monto != null ? String(c.monto) : '' })) : (cfg.cuotaExtraDescripcion ? [{ descripcion: cfg.cuotaExtraDescripcion || '', monto: cfg.cuotaExtraMonto != null ? String(cfg.cuotaExtraMonto) : '', frecuencia: 'monthly' }] : []),
                consumoIndividualizado: parseBoolean(cfg.consumoIndividualizado) || (cfg.consumoTipo && cfg.consumoTipo !== 'no'),
                consumoTipo: cfg.consumoTipo || 'no',
                consumoOpciones: cfg.consumoOpciones || '',
                consumoAgua: parseBoolean(cfg.consumoAgua),
                consumoGas: parseBoolean(cfg.consumoGas),
                consumoInternet: parseBoolean(cfg.consumoInternet),
                consumoLuz: parseBoolean(cfg.consumoLuz),
                consumoAguaMonto: cfg.consumoAguaMonto != null ? String(cfg.consumoAguaMonto) : '',
                consumoGasMonto: cfg.consumoGasMonto != null ? String(cfg.consumoGasMonto) : '',
                consumoInternetMonto: cfg.consumoInternetMonto != null ? String(cfg.consumoInternetMonto) : '',
                consumoLuzMonto: cfg.consumoLuzMonto != null ? String(cfg.consumoLuzMonto) : '',
                consumoServiciosList: Array.isArray(cfg.consumoServiciosList) ? cfg.consumoServiciosList.map((s, i) => ({
                    id: s.id || i,
                    nombre: s.nombre || '',
                    monto: s.monto != null ? String(s.monto) : '',
                    habilitado: s.habilitado !== undefined ? s.habilitado : true
                })) : [],
                fondoReservaPercent: cfg.fondoReservaPercent != null ? String(cfg.fondoReservaPercent) : '10',
                corteDia: cfg.corteDia != null ? String(cfg.corteDia) : '',
                corteDiaResidentes: cfg.corteDiaResidentes != null ? String(cfg.corteDiaResidentes) : '',
                sancionesInicioDia: cfg.sancionesInicioDia != null ? String(cfg.sancionesInicioDia) : '',
                annualBudgetEnabled: parseBoolean(cfg.annualBudgetEnabled, cfg.annualBudget != null && cfg.annualBudget !== ''),
                maintenanceAllocation: cfg.maintenanceAllocation != null ? String(cfg.maintenanceAllocation) : '',
                securityCleaningAllocation: cfg.securityCleaningAllocation != null ? String(cfg.securityCleaningAllocation) : '',
                utilitiesAllocation: cfg.utilitiesAllocation != null ? String(cfg.utilitiesAllocation) : '',
                reserveAllocation: cfg.reserveAllocation != null ? String(cfg.reserveAllocation) : '',
                // descuentos/multas/intereses
                discountEnabled: cfg.discountEnabled || false,
                discountPercent: cfg.discountPercent != null ? String(cfg.discountPercent) : '',
                discountDescription: cfg.discountDescription || '',
                multasEnabled: cfg.multasEnabled || false,
                multasAmount: cfg.multasAmount != null ? String(cfg.multasAmount) : '',
                multasAdministrativas: cfg.multasAdministrativas || '',
                multasList: Array.isArray(cfg.multasAdministrativasList) ? cfg.multasAdministrativasList.map((m, i) => ({ id: m.id || i, descripcion: m.descripcion || '', monto: m.monto != null ? String(m.monto) : '' })) : (cfg.multasAdministrativas ? [{ id: Date.now(), descripcion: cfg.multasAdministrativas || '', monto: cfg.multasAmount != null ? String(cfg.multasAmount) : '' }] : []),
                interesesEnabled: cfg.interesesEnabled || false,
                interesesType: cfg.interesesType || 'fixed',
                interesesValue: cfg.interesesValue != null ? String(cfg.interesesValue) : '',
                interesesMesesPermitidosDeuda: cfg.interesesMesesPermitidosDeuda != null ? String(cfg.interesesMesesPermitidosDeuda) : '1',
                interesesAplicarA: Array.isArray(cfg.interesesAplicarA) ? cfg.interesesAplicarA : [],
                // restricción de servicios
                restriccionServiciosEnabled: cfg.restriccionServiciosEnabled || false,
                restriccionServicios: Array.isArray(cfg.restriccionServicios) ? cfg.restriccionServicios : [],
                restriccionesDetalle: cfg.restriccionesDetalle || null,
                restriccionMeses: cfg.restriccionMeses != null ? String(cfg.restriccionMeses) : '0',
            };
            const modHabilitados = data.modulosHabilitados || {};
            setModulosHabilitados(modHabilitados);
            setConfig((prev) => ({ ...prev, ...normalized }));
            // Guardar baseline normalizada para comparar cambios (evita falsos positivos tras guardar)
            originalConfigRef.current = { ...normalized };
            loadedRef.current = true;
        }, (err) => {
            console.error('ConfiguracionFinanciera: onSnapshot error', err);
        });
        return () => unsub();
    }, [fraccionamientoId]);

    // NO AUTOSAVE: configuration will only be saved when user clicks "Guardar"

    const subtotal = (Number(config.montoMensual) || 0) +
        (config.consumoAgua ? (Number(config.consumoAguaMonto) || 0) : 0) +
        (config.consumoGas ? (Number(config.consumoGasMonto) || 0) : 0) +
        (config.consumoInternet ? (Number(config.consumoInternetMonto) || 0) : 0) +
        (config.consumoLuz ? (Number(config.consumoLuzMonto) || 0) : 0) +
        (config.consumoServiciosList || []).reduce((acc, s) => acc + (s.habilitado ? (Number(s.monto) || 0) : 0), 0);

    const reserveAllocationValue = Number(config.reserveAllocation) || 0;
    const reservaAmount = config.annualBudgetEnabled ? (reserveAllocationValue / 12) : 0;
    const total = subtotal + reservaAmount;

    return (
        <div className="fin-page-container" ref={pageTopRef}>
            <div className="fin-header">
                <div className="fin-header-info">
                    <h1><FaDollarSign /> Configuración Financiera</h1>
                    <p>Configura cuotas, morosidad y otros parámetros financieros del fraccionamiento.</p>
                </div>
                <Link to="/configuraciones" className="fin-back-link">
                    <FaArrowLeft /> Volver a Configuraciones
                </Link>
            </div>

            <div className="fin-tabs-container">
                <button
                    className={`fin-tab-pill ${tab === 0 ? 'active' : ''}`}
                    onClick={() => handleTabChange(0)}
                >
                    Tipos de Cuotas
                </button>
                <button
                    className={`fin-tab-pill ${tab === 1 ? 'active' : ''}`}
                    onClick={() => handleTabChange(1)}
                >
                    Descuentos y Sanciones
                </button>
                <button
                    className={`fin-tab-pill ${tab === 2 ? 'active' : ''}`}
                    onClick={() => handleTabChange(2)}
                >
                    Presupuesto y Fondo
                </button>
                {modulosHabilitados?.modulo_migracion_regularizacion && (
                    <button
                        className={`fin-tab-pill ${tab === 3 ? 'active' : ''}`}
                        onClick={() => handleTabChange(3)}
                    >
                        Migración/Regularización
                    </button>
                )}
            </div>

            <div className="fin-content-wrapper">
                {tab === 0 && (
                    <div className="fin-section-card">
                        <div className="fin-section-title-bar">
                            <div className="fin-section-index">1.</div>
                            <div className="fin-section-labels">
                                <h2>Tipos de Cuotas (Ingresos)</h2>
                                <p>Sistema de administración de condominio</p>
                            </div>
                        </div>

                        <div className="fin-layout-grid">
                            <div className="fin-main-column">
                                <div className="fin-field-group">
                                    <label>Tipo de Cálculo</label>
                                    <select
                                        className="fin-select"
                                        value={config.tipoCalculo}
                                        onChange={(e) => setConfigField('tipoCalculo', e.target.value)}
                                    >
                                        <option value="igual">Cuota Fija (Todos pagan lo mismo)</option>
                                        {/* Ocultos temporalmente según requerimiento: proporcional, custom */}
                                    </select>
                                    <p className="fin-field-description">
                                        {config.tipoCalculo === 'igual' ? 'Se aplica una cuota fija igual para todos los propietarios.' :
                                            config.tipoCalculo === 'proporcional' ? 'La cuota se calcula según el porcentaje de indiviso o metros cuadrados de cada propiedad.' :
                                                'Permite asignar un monto específico de forma manual a cada casa.'}
                                    </p>
                                </div>

                                <div className="fin-summary-card" style={{ background: '#ffffff', border: '1px solid #e2e8f0' }}>
                                    <div className="fin-field-group">
                                        <label style={{ textAlign: 'center', width: '100%', display: 'block' }}>Monto Mensual</label>
                                        <div className="fin-currency-container" style={{ justifyContent: 'center' }}>
                                            <span className="fin-currency-symbol" style={{ position: 'relative', left: 'auto', marginRight: '8px' }}>$</span>
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                className="fin-input"
                                                style={{ maxWidth: '200px', textAlign: 'center' }}
                                                value={config.montoMensual || ''}
                                                onChange={(e) => setConfigField('montoMensual', e.target.value)}
                                            />
                                        </div>
                                        {formErrors && formErrors.montoMensual && <small className="field-error">{formErrors.montoMensual}</small>}
                                        <p className="fin-field-description" style={{ textAlign: 'center' }}>
                                            Monto base mensual que se cobrará a cada unidad según el 'Tipo de Cálculo'. Incluye solo la cuota fija sin extras ni consumos.
                                        </p>
                                    </div>
                                </div>

                                <div className="fin-field-group">
                                    <label>Día de corte mensual</label>
                                    <select
                                        className="fin-select"
                                        value={config.corteDia}
                                        onChange={(e) => setConfigField('corteDia', e.target.value)}
                                    >
                                        <option value="">Selecciona un día</option>
                                        {(() => {
                                            const options = [...Array(28)].map((_, i) => i + 1);
                                            if (config.corteDia && Number(config.corteDia) > 28 && !options.includes(Number(config.corteDia))) {
                                                options.push(Number(config.corteDia));
                                            }
                                            return options.map(day => (
                                                <option key={day} value={day}>{day}</option>
                                            ));
                                        })()}
                                    </select>
                                    {formErrors && formErrors.corteDia && <small className="field-error">{formErrors.corteDia}</small>}
                                    <p className="fin-field-description">
                                        Día del mes en el que se cierra el ciclo de facturación. Determina la fecha límite para calcular saldos y generar cargos.
                                    </p>
                                </div>

                                <div className="fin-field-group">
                                    <label>Día de corte mensual para residentes</label>
                                    <select
                                        className="fin-select"
                                        value={config.corteDiaResidentes || ''}
                                        onChange={(e) => setConfigField('corteDiaResidentes', e.target.value)}
                                    >
                                        <option value="">Selecciona un día (Mismo que corte de facturación)</option>
                                        {(() => {
                                            const options = [...Array(28)].map((_, i) => i + 1);
                                            if (config.corteDiaResidentes && Number(config.corteDiaResidentes) > 28 && !options.includes(Number(config.corteDiaResidentes))) {
                                                options.push(Number(config.corteDiaResidentes));
                                            }
                                            return options.map(day => (
                                                <option key={day} value={day}>{day}</option>
                                            ));
                                        })()}
                                    </select>
                                    <p className="fin-field-description">
                                        Día de corte que se mostrará como informativo a los residentes en su estado de cuenta (sin impacto en los ciclos automáticos de cobros).
                                    </p>
                                </div>

                                <div className="fin-field-group">
                                    <label>Día de inicio de sanciones por retraso</label>
                                    <select
                                        className="fin-select"
                                        value={config.sancionesInicioDia}
                                        onChange={(e) => setConfigField('sancionesInicioDia', e.target.value)}
                                    >
                                        <option value="">Selecciona un día</option>
                                        {(() => {
                                            const options = [...Array(28)].map((_, i) => i + 1);
                                            if (config.sancionesInicioDia && Number(config.sancionesInicioDia) > 28 && !options.includes(Number(config.sancionesInicioDia))) {
                                                options.push(Number(config.sancionesInicioDia));
                                            }
                                            return options.map(day => (
                                                <option 
                                                    key={day} 
                                                    value={day}
                                                    disabled={config.corteDia && Number(day) <= Number(config.corteDia)}
                                                >
                                                    {day} {config.corteDia && Number(day) === Number(config.corteDia) ? '(Día de Corte)' : (config.corteDia && Number(day) < Number(config.corteDia) ? '(Previo al Corte)' : '')}
                                                </option>
                                            ));
                                        })()}
                                    </select>
                                    {formErrors && formErrors.sancionesInicioDia && <small className="field-error">{formErrors.sancionesInicioDia}</small>}
                                    
                                    {config.corteDia && config.sancionesInicioDia && Number(config.sancionesInicioDia) <= Number(config.corteDia) && (
                                        <div style={{
                                            marginTop: '8px',
                                            padding: '10px 14px',
                                            backgroundColor: '#fffbeb',
                                            border: '1px solid #fef3c7',
                                            borderRadius: '8px',
                                            color: '#b45309',
                                            fontSize: '0.85rem',
                                            lineHeight: '1.4',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                        }}>
                                            ⚠️ <strong>Conflicto de Fechas:</strong> El día de inicio de sanciones debe ser posterior al día de corte mensual. Selecciona otra fecha (se recomienda establecerlo al menos 3 días después del corte, por ejemplo el día <strong>{Math.min(28, Number(config.corteDia) + 3)}</strong>).
                                        </div>
                                    )}
                                    <p className="fin-field-description">
                                        Día del mes a partir del cual se consideran vencidos los pagos y pueden empezar a aplicarse multas o intereses.
                                    </p>
                                </div>

                                <div className="fin-field-group">
                                    <label>Cuota Extraordinaria</label>
                                    <p className="fin-field-description">
                                        Cuotas no recurrentes que se cobran por conceptos puntuales (obras, reparaciones, eventos). Puedes agregar varias con su frecuencia.
                                    </p>
                                    <button
                                        type="button"
                                        className="fin-btn-add"
                                        onClick={addCuotaExtra}
                                        style={{
                                            background: '#3b82f6',
                                            color: 'white',
                                            border: 'none',
                                            padding: '10px 20px',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            alignSelf: 'center',
                                            marginTop: '12px'
                                        }}
                                    >
                                        + Agregar cuota extraordinaria
                                    </button>

                                    {(config.cuotaExtras || []).map((extra, index) => {
                                        const isLast = index === config.cuotaExtras.length - 1;
                                        
                                        if (cuotasCollapsed) {
                                            // VISTA MINIATURA (CONTRAÍDA)
                                            return (
                                                <div 
                                                    key={extra.id || index} 
                                                    className="fin-miniature-card" 
                                                    style={{ 
                                                        marginTop: '8px', 
                                                        background: '#f8fafc', 
                                                        border: '1px solid #e2e8f0', 
                                                        borderRadius: '8px', 
                                                        padding: '10px 16px',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <span style={{ background: '#e2e8f0', color: '#64748b', width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 600 }}>{index + 1}</span>
                                                        <span style={{ fontWeight: 600, color: '#1e293b' }}>{extra.descripcion || '(Sin concepto)'}</span>
                                                        <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>•</span>
                                                        <span style={{ fontWeight: 700, color: '#0ea5e9' }}>${Number(extra.monto || 0).toFixed(2)}</span>
                                                        <span style={{ color: '#64748b', fontSize: '0.8rem', background: '#f1f5f9', padding: '2px 8px', borderRadius: '4px' }}>{extra.frecuencia === 'monthly' ? 'Mensual' : 'Periódica'}</span>
                                                    </div>
                                                    <button 
                                                        type="button" 
                                                        className="fin-btn-link" 
                                                        style={{ color: '#3b82f6', fontSize: '0.85rem', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                                                        onClick={() => setCuotasCollapsed(false)}
                                                    >
                                                        Expandir / Editar
                                                    </button>
                                                </div>
                                            );
                                        }

                                        // VISTA COMPLETA (EXPANDIDA)
                                        return (
                                            <div key={extra.id || index} className="fin-summary-card" style={{ marginTop: '12px' }}>
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                                    <div className="fin-field-group">
                                                        <label>Concepto</label>
                                                        <input
                                                            ref={isLast ? lastExtraInputRef : null}
                                                            type="text"
                                                            className="fin-input"
                                                            value={extra.descripcion || ''}
                                                            onChange={(e) => updateCuotaExtra(index, 'descripcion', e.target.value)}
                                                            placeholder="Ej: Impermeabilización"
                                                        />
                                                        {cuotaExtrasErrors[index]?.descripcion && <small className="field-error">{cuotaExtrasErrors[index].descripcion}</small>}
                                                    </div>
                                                     <div className="fin-field-group">
                                                        <label>Monto</label>
                                                        <div className="fin-currency-container">
                                                            <span className="fin-currency-symbol">$</span>
                                                            <input
                                                                type="text"
                                                                inputMode="decimal"
                                                                className="fin-input fin-input-currency"
                                                                value={extra.monto || ''}
                                                                onChange={(e) => updateCuotaExtra(index, 'monto', e.target.value)}
                                                            />
                                                        </div>
                                                        {cuotaExtrasErrors[index]?.monto && <small className="field-error">{cuotaExtrasErrors[index].monto}</small>}
                                                    </div>
                                                </div>
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '12px' }}>
                                                    <div className="fin-field-group">
                                                        <label>Frecuencia de cobro</label>
                                                        <select
                                                            className="fin-select"
                                                            value={extra.frecuencia || 'monthly'}
                                                            onChange={(e) => updateCuotaExtra(index, 'frecuencia', e.target.value)}
                                                        >
                                                            <option value="monthly">Mensual</option>
                                                            <option value="bimonthly">Bimestral</option>
                                                            <option value="quarterly">Trimestral</option>
                                                            <option value="semiannual">Semestral</option>
                                                            <option value="yearly">Anual</option>
                                                        </select>
                                                    </div>
                                                    <div className="fin-field-group">
                                                        <label>Mes de inicio</label>
                                                        <select
                                                            className="fin-select"
                                                            value={extra.mesInicio !== undefined ? extra.mesInicio : new Date().getMonth() + 1}
                                                            onChange={(e) => updateCuotaExtra(index, 'mesInicio', parseInt(e.target.value))}
                                                        >
                                                            <option value={1}>Enero</option>
                                                            <option value={2}>Febrero</option>
                                                            <option value={3}>Marzo</option>
                                                            <option value={4}>Abril</option>
                                                            <option value={5}>Mayo</option>
                                                            <option value={6}>Junio</option>
                                                            <option value={7}>Julio</option>
                                                            <option value={8}>Agosto</option>
                                                            <option value={9}>Septiembre</option>
                                                            <option value={10}>Octubre</option>
                                                            <option value={11}>Noviembre</option>
                                                            <option value={12}>Diciembre</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                {/* Oculto para el rol de administrador (incluye superadmin, admin, etc.) según requerimiento. Solo platformadmin puede ver este botón. */}
                                                {isPlatformAdmin && (
                                                    <button
                                                        type="button"
                                                        className="fin-btn-ghost"
                                                        style={{ marginTop: '16px', color: '#ef4444', borderColor: '#fee2e2' }}
                                                        onClick={() => requestRemoveCuotaExtra(index)}
                                                    >
                                                        Eliminar Concepto
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}

                                    {(config.cuotaExtras || []).length > 0 && (
                                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
                                            <button
                                                type="button"
                                                className="fin-btn"
                                                style={{
                                                    background: 'linear-gradient(135deg, #10b981, #059669)',
                                                    color: 'white',
                                                    padding: '12px 24px',
                                                    borderRadius: '8px',
                                                    border: 'none',
                                                    fontWeight: 700,
                                                    cursor: 'pointer',
                                                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px'
                                                }}
                                                onClick={saveExtraFeesOnly}
                                                disabled={isSaving}
                                            >
                                                {isSaving ? 'Sincronizando...' : 'Guardar y Sincronizar Cuotas Extra'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="fin-sidebar-column">
                                <div className="fin-sidebar-card fin-card-warning">
                                    <div className="fin-sidebar-card-header">
                                        <h4 style={{ margin: 0 }}><FaExclamationTriangle /> Congelar cuentas heredadas</h4>
                                        <label className="fin-switch">
                                            <input
                                                type="checkbox"
                                                checked={Boolean(config.congelarCuentasHeredadas)}
                                                onChange={(e) => handleFreezeToggle(e.target.checked)}
                                            />
                                            <span className="fin-slider" />
                                        </label>
                                    </div>
                                    <p className="fin-card-helper-text">
                                        Al activar esta opción, las casas con adeudo anterior <strong>no recibirán nuevos cargos mensuales</strong> hasta regularizar su situación.
                                    </p>
                                </div>

                                <div className="fin-sidebar-card fin-card-info">
                                    <div className="fin-sidebar-card-header">
                                        <h4 style={{ margin: 0 }}>Cuotas de Consumo</h4>
                                    </div>
                                    <p className="fin-card-helper-text">
                                        Servicios cobrados por la administración (Agua, Gas, etc.)
                                    </p>
                                    <div className="fin-field-group">
                                        <label>Modo de cobro</label>
                                        <select
                                            className="fin-select"
                                            value={config.consumoTipo}
                                            onChange={(e) => setConfigField('consumoTipo', e.target.value)}
                                        >
                                            <option value="no">No aplicar</option>
                                            <option value="fixed">Monto Fijo Mensual</option>
                                            {/* Oculto temporalmente según requerimiento: units */}
                                        </select>
                                    </div>

                                    {config.consumoTipo !== 'no' && (
                                        <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            {/* Servicios predefined (backward compatibility) */}
                                            <div className="fin-switch-container" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <label className="fin-switch">
                                                    <input
                                                        type="checkbox"
                                                        checked={Boolean(config.consumoAgua)}
                                                        onChange={(e) => setConfig((p) => ({ ...p, consumoAgua: e.target.checked }))}
                                                    />
                                                    <span className="fin-slider" />
                                                </label>
                                                <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>Agua</span>
                                                {config.consumoAgua && (
                                                    <div className="fin-currency-container" style={{ marginLeft: 'auto', width: '90px' }}>
                                                        <span className="fin-currency-symbol">$</span>
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            className="fin-input fin-input-currency"
                                                            style={{ padding: '8px 8px 8px 24px', fontSize: '0.85rem' }}
                                                            value={config.consumoAguaMonto || ''}
                                                            onChange={(e) => setConfigField('consumoAguaMonto', e.target.value)}
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            <div className="fin-switch-container" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <label className="fin-switch">
                                                    <input
                                                        type="checkbox"
                                                        checked={Boolean(config.consumoGas)}
                                                        onChange={(e) => setConfig((p) => ({ ...p, consumoGas: e.target.checked }))}
                                                    />
                                                    <span className="fin-slider" />
                                                </label>
                                                <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>Gas</span>
                                                {config.consumoGas && (
                                                    <div className="fin-currency-container" style={{ marginLeft: 'auto', width: '90px' }}>
                                                        <span className="fin-currency-symbol">$</span>
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            className="fin-input fin-input-currency"
                                                            style={{ padding: '8px 8px 8px 24px', fontSize: '0.85rem' }}
                                                            value={config.consumoGasMonto || ''}
                                                            onChange={(e) => setConfigField('consumoGasMonto', e.target.value)}
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            {/* Servicios dinámicos */}
                                            {(config.consumoServiciosList || []).map((servicio, idx) => (
                                                <div key={servicio.id || idx} className="fin-dynamic-service-row">
                                                    <div className="fin-switch-container" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <label className="fin-switch">
                                                            <input
                                                                type="checkbox"
                                                                checked={Boolean(servicio.habilitado)}
                                                                onChange={(e) => updateConsumoServicio(idx, 'habilitado', e.target.checked)}
                                                            />
                                                            <span className="fin-slider" />
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="fin-input-minimal"
                                                            placeholder="Nombre del servicio"
                                                            value={servicio.nombre || ''}
                                                            onChange={(e) => updateConsumoServicio(idx, 'nombre', e.target.value)}
                                                            style={{ fontSize: '0.9rem', fontWeight: 600, border: 'none', background: 'transparent', width: '100px' }}
                                                        />
                                                        {servicio.habilitado && (
                                                            <div className="fin-currency-container" style={{ marginLeft: 'auto', width: '90px' }}>
                                                                <span className="fin-currency-symbol" style={{ left: '8px' }}>$</span>
                                                                <input
                                                                    type="text"
                                                                    inputMode="decimal"
                                                                    className="fin-input fin-input-currency"
                                                                    style={{ padding: '8px 8px 8px 20px', fontSize: '0.85rem' }}
                                                                    value={servicio.monto || ''}
                                                                    onChange={(e) => updateConsumoServicio(idx, 'monto', e.target.value)}
                                                                />
                                                            </div>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={() => removeConsumoServicio(idx)}
                                                            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px' }}
                                                        >
                                                            <FaTrash size={12} />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}

                                            <button
                                                type="button"
                                                className="fin-btn-add-minimal"
                                                onClick={addConsumoServicio}
                                                style={{
                                                    marginTop: '8px',
                                                    fontSize: '0.8rem',
                                                    color: '#3b82f6',
                                                    background: 'none',
                                                    border: '1px dashed #3b82f6',
                                                    borderRadius: '4px',
                                                    padding: '4px 8px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                + Agregar servicio
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <div className="fin-summary-card">
                                    <h4 style={{ margin: '0 0 16px 0', fontSize: '1.1rem' }}>Resumen Mensual</h4>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.95rem' }}>
                                        <span>Subtotal</span>
                                        <strong>${subtotal.toFixed(2)}</strong>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', fontSize: '0.95rem' }}>
                                        <span>Fondo de Reserva</span>
                                        <strong>${reservaAmount.toFixed(2)}</strong>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px', borderTop: '2px solid #e2e8f0' }}>
                                        <span style={{ fontWeight: 700 }}>Total Estimado</span>
                                        <strong style={{ color: '#3b82f6', fontSize: '1.2rem' }}>${total.toFixed(2)}</strong>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="fin-actions-footer" style={{
                            padding: '24px 32px',
                            borderTop: '1px solid #e2e8f0',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '16px',
                            background: '#f8fafc'
                        }}>
                            <button
                                className="fin-btn"
                                style={{
                                    padding: '10px 24px',
                                    borderRadius: '8px',
                                    border: '1px solid #cbd5e1',
                                    background: 'white',
                                    color: '#64748b',
                                    fontWeight: 600,
                                    cursor: 'pointer'
                                }}
                                onClick={handleCancelClick}
                                disabled={isSaving}
                            >
                                Descartar
                            </button>
                            <button
                                className="fin-btn"
                                style={{
                                    padding: '10px 32px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
                                    color: 'white',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                                }}
                                onClick={saveNow}
                                disabled={isSaving}
                            >
                                {isSaving ? 'Guardando...' : 'Guardar Todo'}
                            </button>
                        </div>
                    </div>
                )}

                {tab === 1 && (
                    <div className="fin-section-card">
                        <div className="fin-section-title-bar">
                            <div className="fin-section-index">2.</div>
                            <div className="fin-section-labels">
                                <h2>Descuentos y Sanciones</h2>
                                <p>Configura beneficios por pago anticipado y penalizaciones por mora</p>
                            </div>
                        </div>

                        <div className="fin-layout-grid">
                            <div className="fin-main-column">
                                <div className="fin-field-group">
                                    <div className="fin-switch-container">
                                        <label className="fin-switch">
                                            <input
                                                type="checkbox"
                                                checked={Boolean(config.discountEnabled)}
                                                onChange={(e) => setConfigField('discountEnabled', e.target.checked)}
                                            />
                                            <span className="fin-slider" />
                                        </label>
                                        <div>
                                            <span className="fin-switch-label" style={{ display: 'block' }}>Habilitar descuento por pronto pago</span>
                                            <p className="fin-field-description" style={{ margin: 0 }}>Activa para permitir descuentos automáticos antes del día de corte.</p>
                                        </div>
                                    </div>
                                </div>

                                {config.discountEnabled && (
                                    <div className="fin-field-group">
                                        <label>Porcentaje de Descuento (%)</label>
                                        <div className="fin-input-wrapper" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                className="fin-input"
                                                style={{ paddingRight: '40px' }}
                                                value={config.discountPercent || ''}
                                                onChange={(e) => setConfigField('discountPercent', e.target.value)}
                                            />
                                            <span style={{ position: 'absolute', right: '18px', color: '#64748b', fontWeight: 600 }}>%</span>
                                        </div>
                                        {formErrors && formErrors.discountPercent && <small className="field-error">{formErrors.discountPercent}</small>}
                                    </div>
                                )}

                                <div className="fin-field-group" style={{ marginTop: '20px' }}>
                                    <div className="fin-switch-container">
                                        <label className="fin-switch">
                                            <input
                                                type="checkbox"
                                                checked={Boolean(config.interesesEnabled)}
                                                onChange={(e) => handleInterestToggle(e.target.checked)}
                                            />
                                            <span className="fin-slider" />
                                        </label>
                                        <div>
                                            <span className="fin-switch-label" style={{ display: 'block' }}>Habilitar intereses moratorios</span>
                                            <p className="fin-field-description" style={{ margin: 0 }}>Se aplican automáticamente después del día de inicio de sanciones.</p>
                                        </div>
                                    </div>
                                </div>

                                {config.interesesEnabled && (
                                    <div className="fin-summary-card" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                        <div className="fin-field-group">
                                            <label>Tipo de Interés</label>
                                            <select
                                                className="fin-select"
                                                value={config.interesesType}
                                                onChange={(e) => setConfigField('interesesType', e.target.value)}
                                            >
                                                <option value="fixed">Monto Fijo Mensual</option>
                                                <option value="percent_simple">Porcentaje Simple</option>
                                                <option value="percent_compound">Porcentaje Compuesto</option>
                                            </select>
                                        </div>

                                        <div className="fin-field-group">
                                            <label>Valor ({config.interesesType === 'fixed' ? 'Monto' : '%'})</label>
                                            <div className="fin-currency-container">
                                                <span className="fin-currency-symbol">{config.interesesType === 'fixed' ? '$' : '%'}</span>
                                                <input
                                                    type="text"
                                                    className="fin-input fin-input-currency"
                                                    value={config.interesesValue || ''}
                                                    onChange={(e) => setConfigField('interesesValue', e.target.value)}
                                                    placeholder={config.interesesType === 'fixed' ? 'Ej: 50' : 'Ej: 2.5'}
                                                />
                                            </div>
                                            {formErrors && formErrors.interesesValue && <small className="field-error">{formErrors.interesesValue}</small>}
                                        </div>

                                        <div className="fin-field-group">
                                            <label>Meses de gracia permitidos</label>
                                            <input
                                                type="number"
                                                min="0"
                                                max="12"
                                                className="fin-input"
                                                value={config.interesesMesesPermitidosDeuda ?? '0'}
                                                onChange={(e) => setConfigField('interesesMesesPermitidosDeuda', e.target.value)}
                                            />
                                            <p className="fin-field-description">
                                                Número de meses de adeudo antes de aplicar el primer cargo por interés.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="fin-sidebar-column">
                                <div className="fin-field-group">
                                    <div className="fin-switch-container">
                                        <label className="fin-switch">
                                            <input
                                                type="checkbox"
                                                checked={Boolean(config.multasEnabled)}
                                                onChange={(e) => setConfigField('multasEnabled', e.target.checked)}
                                            />
                                            <span className="fin-slider" />
                                        </label>
                                        <span className="fin-switch-label">Habilitar Sanciones Administrativas</span>
                                    </div>
                                </div>

                                {config.multasEnabled && (
                                    <div className="fin-sidebar-card fin-card-info">
                                        <h4 style={{ margin: '0 0 12px 0' }}>Sanciones Registradas</h4>
                                        <button
                                            type="button"
                                            className="fin-btn-add"
                                            style={{ width: '100%', marginBottom: '16px' }}
                                            onClick={() => setShowAddMulta(true)}
                                        >
                                            + Nueva Sanción
                                        </button>

                                        {showAddMulta && (
                                            <div className="fin-summary-card" style={{ marginBottom: '16px', background: 'white' }}>
                                                <div className="fin-field-group">
                                                    <label>Descripción</label>
                                                    <input
                                                        type="text"
                                                        className="fin-input"
                                                        style={{ padding: '8px 12px' }}
                                                        value={newMultaDesc}
                                                        onChange={(e) => setNewMultaDesc(e.target.value)}
                                                    />
                                                </div>
                                                <div className="fin-field-group" style={{ marginTop: '8px' }}>
                                                    <label>Monto</label>
                                                    <div className="fin-currency-container">
                                                        <span className="fin-currency-symbol">$</span>
                                                        <input
                                                            type="text"
                                                            className="fin-input fin-input-currency"
                                                            style={{ padding: '8px 8px 8px 24px' }}
                                                            value={newMultaMonto}
                                                            onChange={(e) => setNewMultaMonto(e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                                                    <button className="fin-btn-primary" style={{ flex: 1, fontSize: '0.85rem', padding: '6px' }} onClick={() => {
                                                        if (!newMultaDesc || !newMultaMonto) return;
                                                        const nueva = { id: Date.now(), descripcion: newMultaDesc, monto: newMultaMonto };
                                                        setConfig(prev => ({ ...prev, multasList: [...(prev.multasList || []), nueva] }));
                                                        setShowAddMulta(false); setNewMultaDesc(''); setNewMultaMonto('');
                                                    }}>Guardar</button>
                                                    <button className="fin-btn-ghost" style={{ flex: 1, fontSize: '0.85rem', padding: '6px' }} onClick={() => setShowAddMulta(false)}>Cancelar</button>
                                                </div>
                                            </div>
                                        )}

                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            {(config.multasList || []).map((multa, idx) => (
                                                <div key={multa.id || idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                        <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{multa.descripcion}</span>
                                                        <span style={{ color: '#3b82f6', fontWeight: 700, fontSize: '0.85rem' }}>${multa.monto}</span>
                                                    </div>
                                                    <button onClick={() => requestRemoveMulta(idx)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}><FaTrash /></button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="fin-field-group" style={{ marginTop: '20px' }}>
                                    <div className="fin-switch-container">
                                        <label className="fin-switch">
                                            <input
                                                type="checkbox"
                                                checked={Boolean(config.restriccionServiciosEnabled)}
                                                onChange={(e) => {
                                                    const active = e.target.checked;
                                                    setConfigField('restriccionServiciosEnabled', active);
                                                    if (active) {
                                                        setShowRestriccionesModal(true);
                                                    }
                                                }}
                                            />
                                            <span className="fin-slider" />
                                        </label>
                                        <span className="fin-switch-label">Restringir servicios por mora</span>
                                    </div>
                                </div>

                                {config.restriccionServiciosEnabled && (
                                    <div className="fin-sidebar-card fin-card-warning" style={{ borderRadius: '14px', border: '1px solid #fed7aa', background: '#fffaf5', padding: '16px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                            <h4 style={{ margin: 0, color: '#9a3412', fontSize: '0.98rem', fontWeight: 700 }}>
                                                Control Granular de Restricciones
                                            </h4>
                                            <span style={{ fontSize: '0.78rem', background: '#ffedd5', color: '#c2410c', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>
                                                {config.restriccionMeses === '0' || config.restriccionMeses === 0 ? 'Tolerancia 0 meses' : `${config.restriccionMeses || 1} meses`}
                                            </span>
                                        </div>
                                        <p style={{ margin: '0 0 14px 0', fontSize: '0.84rem', color: '#7c2d12', lineHeight: 1.45 }}>
                                            Personaliza qué pestañas o subsecciones (Comunidad, Amenidades, Vigilancia y Finanzas) se bloquean a los residentes morosos.
                                        </p>

                                        <button
                                            type="button"
                                            className="fin-btn"
                                            style={{
                                                width: '100%',
                                                padding: '10px 14px',
                                                borderRadius: '10px',
                                                border: '1.5px solid #ea580c',
                                                background: '#ffffff',
                                                color: '#ea580c',
                                                fontWeight: 700,
                                                fontSize: '0.88rem',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '8px',
                                                transition: 'all 0.2s ease',
                                                boxShadow: '0 2px 6px rgba(234, 88, 12, 0.1)'
                                            }}
                                            onClick={() => setShowRestriccionesModal(true)}
                                        >
                                            ⚙️ Configurar restricciones de acceso
                                        </button>
                                    </div>
                                )}

                                <ModalRestriccionServiciosMora
                                    isOpen={showRestriccionesModal}
                                    onClose={() => setShowRestriccionesModal(false)}
                                    initialDetalle={config.restriccionesDetalle}
                                    initialMeses={config.restriccionMeses}
                                    onApply={(res) => {
                                        setConfig(prev => ({
                                            ...prev,
                                            restriccionesDetalle: res.restriccionesDetalle,
                                            restriccionMeses: String(res.restriccionMeses),
                                            restriccionServicios: res.restriccionServicios
                                        }));
                                    }}
                                />
                            </div>
                        </div>

                        <div className="fin-actions-footer" style={{ padding: '24px 32px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '16px', background: '#f8fafc' }}>
                            <button className="fin-btn" style={{ padding: '10px 24px', borderRadius: '8px', border: '1px solid #cbd5e1', background: 'white', color: '#64748b', fontWeight: 600, cursor: 'pointer' }} onClick={handleCancelClick} disabled={isSaving}>Descartar</button>
                            <button className="fin-btn" style={{ padding: '10px 32px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #3b82f6, #6366f1)', color: 'white', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)' }} onClick={saveNow} disabled={isSaving}>{isSaving ? 'Guardando...' : 'Guardar Todo'}</button>
                        </div>
                    </div>
                )}

                {tab === 2 && (
                    <div className="fin-section-card">
                        <div className="fin-section-title-bar">
                            <div className="fin-section-index">3.</div>
                            <div className="fin-section-labels">
                                <h2>Presupuesto y Fondo de Reserva</h2>
                                <p>Planificación financiera anual y fondo de contingencia</p>
                            </div>
                        </div>

                        <div className="fin-layout-grid">
                            <div className="fin-main-column">
                                <div className="fin-field-group">
                                    <div className="fin-switch-container">
                                        <label className="fin-switch">
                                            <input
                                                type="checkbox"
                                                checked={Boolean(config.annualBudgetEnabled)}
                                                onChange={(e) => setConfigField('annualBudgetEnabled', e.target.checked)}
                                            />
                                            <span className="fin-slider" />
                                        </label>
                                        <div>
                                            <span className="fin-switch-label" style={{ display: 'block' }}>Habilitar gestión de presupuesto anual</span>
                                            <p className="fin-field-description" style={{ margin: 0 }}>Permite proyectar gastos y calcular el fondo de reserva automáticamente.</p>
                                        </div>
                                    </div>
                                </div>

                                {config.annualBudgetEnabled && (
                                    <div className="fin-summary-card" style={{ display: 'flex', flexDirection: 'column', gap: '24px', background: 'white' }}>
                                        <div className="fin-field-group">
                                            <label>Presupuesto Total Operativo (Anual)</label>
                                            <div className="fin-currency-container">
                                                <span className="fin-currency-symbol">$</span>
                                                <input
                                                    type="text"
                                                    className="fin-input fin-input-currency"
                                                    style={{ background: '#f8fafc', fontWeight: 700, fontSize: '1.2rem' }}
                                                    value={(
                                                        parseFloat(config.maintenanceAllocation || 0) +
                                                        parseFloat(config.securityCleaningAllocation || 0) +
                                                        parseFloat(config.utilitiesAllocation || 0)
                                                    ).toFixed(2)}
                                                    readOnly
                                                />
                                            </div>
                                            <p className="fin-field-description">Suma automática de mantenimiento, seguridad y servicios.</p>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                            <div className="fin-field-group">
                                                <label>Mantenimiento</label>
                                                <div className="fin-currency-container">
                                                    <span className="fin-currency-symbol">$</span>
                                                    <input
                                                        type="text"
                                                        className="fin-input fin-input-currency"
                                                        value={config.maintenanceAllocation || ''}
                                                        onChange={(e) => setConfigField('maintenanceAllocation', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="fin-field-group">
                                                <label>Seguridad y Limpieza</label>
                                                <div className="fin-currency-container">
                                                    <span className="fin-currency-symbol">$</span>
                                                    <input
                                                        type="text"
                                                        className="fin-input fin-input-currency"
                                                        value={config.securityCleaningAllocation || ''}
                                                        onChange={(e) => setConfigField('securityCleaningAllocation', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="fin-field-group">
                                            <label>Servicios (Agua, Luz, Áreas Comunes)</label>
                                            <div className="fin-currency-container">
                                                <span className="fin-currency-symbol">$</span>
                                                <input
                                                    type="text"
                                                    className="fin-input fin-input-currency"
                                                    value={config.utilitiesAllocation || ''}
                                                    onChange={(e) => setConfigField('utilitiesAllocation', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="fin-field-group" style={{ marginTop: '20px' }}>
                                    <label>Política de Uso del Fondo</label>
                                    <textarea
                                        className="fin-textarea"
                                        rows="4"
                                        placeholder="Describe cuándo y cómo usar el fondo de reserva..."
                                        value={config.fundUsePolicy || ''}
                                        onChange={(e) => setConfigField('fundUsePolicy', e.target.value)}
                                    />
                                    <p className="fin-field-description">Reglas internas para el manejo del fondo de contingencia.</p>
                                </div>
                            </div>

                            <div className="fin-sidebar-column">
                                <div className="fin-sidebar-card fin-card-info">
                                    <h4 style={{ margin: '0 0 16px 0' }}>Fondo de Reserva</h4>
                                    <div className="fin-field-group">
                                        <label>Porcentaje Anual (%)</label>
                                        <div className="fin-input-wrapper" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                className="fin-input"
                                                style={{ paddingRight: '40px' }}
                                                value={config.fondoReservaPercent || ''}
                                                onChange={(e) => {
                                                    const percent = e.target.value;
                                                    setConfigField('fondoReservaPercent', percent);
                                                    const operativeTotal = parseFloat(config.maintenanceAllocation || 0) +
                                                        parseFloat(config.securityCleaningAllocation || 0) +
                                                        parseFloat(config.utilitiesAllocation || 0);
                                                    const reserveAmount = (operativeTotal * parseFloat(percent || 0)) / 100;
                                                    setConfig((prev) => ({ ...prev, reserveAllocation: reserveAmount.toFixed(2) }));
                                                }}
                                            />
                                            <span style={{ position: 'absolute', right: '18px', color: '#64748b', fontWeight: 600 }}>%</span>
                                        </div>
                                    </div>

                                    <div className="fin-summary-card" style={{ marginTop: '20px', background: 'white' }}>
                                        <label style={{ fontSize: '0.85rem', color: '#64748b' }}>Asignación al Fondo (Anual)</label>
                                        <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f172a', marginTop: '4px' }}>
                                            ${parseFloat(config.reserveAllocation || 0).toLocaleString()}
                                        </div>
                                        <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e2e8f0' }}>
                                            Monto mensual: <strong>${(parseFloat(config.reserveAllocation || 0) / 12).toFixed(2)}</strong>
                                        </div>
                                    </div>
                                </div>

                                {(() => {
                                    const mant = parseFloat(config.maintenanceAllocation || 0);
                                    const sec = parseFloat(config.securityCleaningAllocation || 0);
                                    const serv = parseFloat(config.utilitiesAllocation || 0);
                                    const opTotal = mant + sec + serv;
                                    const res = parseFloat(config.reserveAllocation || 0);
                                    const annualTotal = opTotal + res;

                                    const getPct = (val) => annualTotal > 0 ? (val / annualTotal * 100).toFixed(1) : 0;

                                    return (
                                        <div className="fin-calc-card">
                                            <h3 className="fin-calc-title">Calculadora de Distribución</h3>
                                            <span className="fin-calc-subtitle">Desglose automático según los valores configurados</span>

                                            <div className="fin-calc-body">
                                                <div className="fin-calc-row">
                                                    <span className="fin-calc-label">Mantenimiento:</span>
                                                    <span className="fin-calc-value">${mant.toLocaleString()}</span>
                                                </div>
                                                <div className="fin-calc-row">
                                                    <span className="fin-calc-label">Seguridad y Limpieza:</span>
                                                    <span className="fin-calc-value">${sec.toLocaleString()}</span>
                                                </div>
                                                <div className="fin-calc-row">
                                                    <span className="fin-calc-label">Servicios (Agua/Luz/Áreas):</span>
                                                    <span className="fin-calc-value">${serv.toLocaleString()}</span>
                                                </div>

                                                <div className="fin-calc-total-row fin-calc-row">
                                                    <span className="fin-calc-label">Total Operativo:</span>
                                                    <span className="fin-calc-value">${opTotal.toLocaleString()}</span>
                                                </div>

                                                <div className="fin-calc-row">
                                                    <span className="fin-calc-label">Fondo de Reserva ({config.fondoReservaPercent || 0}%):</span>
                                                    <span className="fin-calc-value">${res.toLocaleString()}</span>
                                                </div>

                                                <div className="fin-calc-grand-total fin-calc-row">
                                                    <span className="fin-calc-label">Presupuesto Total Anual:</span>
                                                    <span className="fin-calc-value">${annualTotal.toLocaleString()}</span>
                                                </div>
                                            </div>

                                            <div className="fin-calc-viz">
                                                <div className="fin-calc-bar">
                                                    {mant > 0 && <div className="fin-calc-segment mantenimiento" style={{ width: `${getPct(mant)}%` }}>{getPct(mant)}%</div>}
                                                    {sec > 0 && <div className="fin-calc-segment seguridad" style={{ width: `${getPct(sec)}%` }}>{getPct(sec)}%</div>}
                                                    {serv > 0 && <div className="fin-calc-segment servicios" style={{ width: `${getPct(serv)}%` }}>{getPct(serv)}%</div>}
                                                    {res > 0 && <div className="fin-calc-segment reserva" style={{ width: `${getPct(res)}%` }}>{getPct(res)}%</div>}
                                                </div>

                                                <div className="fin-calc-legend">
                                                    <div className="fin-legend-item"><div className="fin-legend-dot mantenimiento" /> Mantenimiento</div>
                                                    <div className="fin-legend-item"><div className="fin-legend-dot seguridad" /> Seguridad/Limpieza</div>
                                                    <div className="fin-legend-item"><div className="fin-legend-dot servicios" /> Servicios</div>
                                                    <div className="fin-legend-item"><div className="fin-legend-dot reserva" /> Fondo Reserva</div>
                                                </div>
                                            </div>

                                            <p className="fin-calc-footer-note">
                                                Los porcentajes se calculan automáticamente en base al presupuesto total.
                                            </p>
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>

                        <div className="fin-actions-footer" style={{ padding: '24px 32px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '16px', background: '#f8fafc' }}>
                            <button className="fin-btn" style={{ padding: '10px 24px', borderRadius: '8px', border: '1px solid #cbd5e1', background: 'white', color: '#64748b', fontWeight: 600, cursor: 'pointer' }} onClick={handleCancelClick} disabled={isSaving}>Descartar</button>
                            <button className="fin-btn" style={{ padding: '10px 32px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #3b82f6, #6366f1)', color: 'white', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)' }} onClick={saveNow} disabled={isSaving}>{isSaving ? 'Guardando...' : 'Guardar Todo'}</button>
                        </div>
                    </div>
                )}

                {tab === 3 && modulosHabilitados?.modulo_migracion_regularizacion && (
                    <MigracionMesesPendientes />
                )}

            </div> {/* Closes fin-content-wrapper */}

            {/* action buttons and dialogs must be inside the main return fragment */}
            <ConfirmDialog
                open={showCancelConfirm}
                title="Descartar cambios"
                message="Estás a punto de descartar los cambios no guardados. ¿Deseas continuar?"
                cancelLabel="Volver"
                confirmLabel="Descartar"
                tone="danger"
                theme="premium"
                confirmDisabled={isSaving}
                onCancel={() => setShowCancelConfirm(false)}
                onConfirm={confirmDiscardChanges}
            />
            <ConfirmDialog
                open={pendingCuotaRemoval !== null}
                title="Eliminar cuota extraordinaria"
                message="Esta acción quitará la cuota extraordinaria seleccionada. Podrás volver a crearla más tarde."
                cancelLabel="Cancelar"
                confirmLabel="Eliminar"
                tone="danger"
                theme="premium"
                onCancel={cancelRemoveCuotaExtra}
                onConfirm={confirmRemoveCuotaExtra}
            />
            <ConfirmDialog
                open={pendingMultaRemoval !== null}
                title="Eliminar sanción administrativa"
                message="La sanción administrativa se eliminará de la configuración. ¿Deseas continuar?"
                cancelLabel="Cancelar"
                confirmLabel="Eliminar"
                tone="danger"
                theme="premium"
                onCancel={cancelRemoveMulta}
                onConfirm={confirmRemoveMulta}
            />
            <ConfirmDialog
                open={showTabChangeConfirm}
                title="Cambios sin guardar"
                message="Tienes cambios sin guardar. Si cambias de pestaña, se perderán estos cambios. ¿Deseas continuar?"
                cancelLabel="Cancelar"
                confirmLabel="Continuar sin guardar"
                tone="warning"
                theme="premium"
                onCancel={cancelTabChange}
                onConfirm={confirmTabChange}
            />
            <ConfirmDialog
                open={showFreezeConfirm}
                title="⚠️ Confirmar congelamiento de cuentas"
                message={
                    <div style={{ textAlign: 'left', lineHeight: '1.6' }}>
                        <p style={{ marginBottom: '12px' }}>
                            <strong>Al activar el congelamiento de cuentas heredadas:</strong>
                        </p>
                        <ul style={{ paddingLeft: '20px', margin: '0 0 12px 0' }}>
                            <li style={{ marginBottom: '8px' }}>
                                Se <strong>congela la deuda histórica</strong> (adeudo heredado de sistemas anteriores)
                                evitando que acumule más <strong>intereses sin control</strong>.
                            </li>
                            <li style={{ marginBottom: '8px' }}>
                                Los <strong>cargos mensuales SÍ se aplican</strong> de ahora en adelante de la misma manera para todas las casas.
                            </li>
                            <li style={{ marginBottom: '8px' }}>
                                Estas casas <strong>recibirán los mismos cargos</strong> que el resto de residentes (cuota mensual, consumos, etc.)
                                <strong> desde el primer mes</strong> de activación.
                            </li>
                            <li style={{ marginBottom: '8px' }}>
                                La deuda histórica se mantiene congelada, pero los nuevos cargos se aplican normalmente.
                            </li>
                        </ul>
                        <p style={{ color: '#856404', fontWeight: 'bold', marginBottom: '0' }}>
                            ¿Deseas activar el congelamiento de cuentas heredadas?
                        </p>
                    </div>
                }
                cancelLabel="Cancelar"
                confirmLabel="Activar congelamiento"
                tone="warning"
                onCancel={cancelFreeze}
                onConfirm={confirmFreeze}
            />
            <ConfirmDialog
                open={showInterestInfoModal}
                title="📋 Configuración de Intereses Moratorios"
                message={
                    <div style={{ textAlign: 'left', lineHeight: '1.6', maxHeight: '60vh', overflowY: 'auto' }}>
                        <p style={{ marginBottom: '12px', fontWeight: 'bold', fontSize: '15px' }}>
                            Al habilitar intereses moratorios, debes configurar las siguientes opciones:
                        </p>

                        <div style={{ backgroundColor: '#f8f9fa', padding: '12px', borderRadius: '6px', marginBottom: '12px' }}>
                            <p style={{ fontWeight: 'bold', marginBottom: '8px', color: '#495057' }}>
                                ⚙️ Tipos de Interés Disponibles:
                            </p>
                            <ul style={{ paddingLeft: '20px', margin: '0', color: '#212529' }}>
                                <li style={{ marginBottom: '8px' }}>
                                    <strong>Monto Fijo:</strong> Se cobra una cantidad fija mensual (ej: $50 por mes de atraso)
                                </li>
                                <li style={{ marginBottom: '8px' }}>
                                    <strong>Porcentaje Simple:</strong> Se aplica un % sobre el capital adeudado una sola vez por mes
                                    (ej: 10% = $10 por cada $100 adeudados cada mes)
                                </li>
                                <li style={{ marginBottom: '0' }}>
                                    <strong>Porcentaje Compuesto:</strong> El interés se capitaliza mensualmente sobre el adeudo total
                                    (ej: Mes 1: 10% de $100 = $10 → Mes 2: 10% de $210 = $21)
                                </li>
                            </ul>
                        </div>

                        <div style={{ backgroundColor: '#fff3cd', padding: '12px', borderRadius: '6px', marginBottom: '12px', border: '1px solid #ffc107' }}>
                            <p style={{ fontWeight: 'bold', marginBottom: '8px', color: '#856404' }}>
                                📅 Período de Gracia (Meses Permitidos):
                            </p>
                            <ul style={{ paddingLeft: '20px', margin: '0', color: '#856404' }}>
                                <li style={{ marginBottom: '8px' }}>
                                    Los <strong>residentes AL CORRIENTE</strong> que empiecen a adeudar tendrán el período de gracia configurado
                                    (ejemplo: 3 meses = no se cobran intereses hasta el mes 4)
                                </li>
                                <li style={{ marginBottom: '8px' }}>
                                    Durante el período de gracia, el residente ve solo el capital adeudado <strong>hasta el día de inicio de sanciones</strong>
                                </li>
                                <li style={{ marginBottom: '0' }}>
                                    Una vez pasado el día de inicio de sanciones, se muestran los intereses acumulados de todos los meses
                                </li>
                            </ul>
                        </div>

                        <div style={{ backgroundColor: '#f8d7da', padding: '12px', borderRadius: '6px', marginBottom: '12px', border: '1px solid #dc3545' }}>
                            <p style={{ fontWeight: 'bold', marginBottom: '8px', color: '#721c24' }}>
                                ⚠️ Reglas para Residentes Morosos:
                            </p>
                            <ul style={{ paddingLeft: '20px', margin: '0', color: '#721c24' }}>
                                <li style={{ marginBottom: '8px' }}>
                                    Los residentes que <strong>YA TIENEN ADEUDO</strong> (morosos/importados con deuda) <strong>NO tienen período de gracia</strong>
                                </li>
                                <li style={{ marginBottom: '8px' }}>
                                    Los intereses se aplican <strong>desde el primer mes</strong> en que se active esta configuración
                                </li>
                                <li style={{ marginBottom: '8px' }}>
                                    <strong>Excepción:</strong> Si activas "Congelar Cuentas Heredadas con Adeudo", esas casas NO acumularán intereses
                                    sobre su deuda histórica, pero sí sobre nuevos cargos
                                </li>
                                <li style={{ marginBottom: '0' }}>
                                    El interés siempre se muestra completo (capital + interés) sin ocultar nada
                                </li>
                            </ul>
                        </div>

                        <div style={{ backgroundColor: '#d1ecf1', padding: '12px', borderRadius: '6px', marginBottom: '12px', border: '1px solid #17a2b8' }}>
                            <p style={{ fontWeight: 'bold', marginBottom: '8px', color: '#0c5460' }}>
                                📊 Ejemplo Práctico (Cuota $100, 3 meses gracia, 10% simple, día sanción: 10):
                            </p>
                            <div style={{ color: '#0c5460', fontSize: '14px' }}>
                                <p style={{ margin: '4px 0' }}><strong>Residente AL CORRIENTE que empieza a adeudar:</strong></p>
                                <ul style={{ paddingLeft: '20px', margin: '4px 0 8px 0' }}>
                                    <li>Antes del día 10: Mes 1 = $100, Mes 2 = $200, Mes 3 = $300</li>
                                    <li>Después del día 10 del Mes 3: $330 (300 capital + 30 interés)</li>
                                    <li>Mes 4: $440 (400 capital + 40 interés total)</li>
                                </ul>
                                <p style={{ margin: '8px 0 4px 0' }}><strong>Residente MOROSO (ya tiene deuda):</strong></p>
                                <ul style={{ paddingLeft: '20px', margin: '4px 0 0 0' }}>
                                    <li>Mes 1: $110 (100 capital + 10 interés inmediato)</li>
                                    <li>Mes 2: $220 (200 capital + 20 interés total)</li>
                                    <li>Sin período de gracia, todo visible desde el inicio</li>
                                </ul>
                            </div>
                        </div>

                        <p style={{ marginBottom: '0', fontWeight: 'bold', color: '#ff6b35', fontSize: '15px', backgroundColor: '#fff3e0', padding: '10px', borderRadius: '6px', border: '2px solid #ff6b35' }}>
                            ⚠️ Debes seleccionar UN tipo de interés y configurar el porcentaje/monto según las reglas de tu fraccionamiento.
                        </p>
                    </div>
                }
                cancelLabel="Cancelar"
                confirmLabel="Entendido, Activar"
                tone="info"
                onCancel={cancelInterestActivation}
                onConfirm={confirmInterestActivation}
            />
        </div>
    );
};

export default ConfiguracionFinanciera;
